import { Membership } from '@/constants';
import { useToast } from './use-toast';
import { apiFetch } from '@/utils';
import { useEffect, useMemo, useState } from 'react';

export const useGetActiveMemberships = (walletAddress: `0x${string}`) => {
  const { toast } = useToast();
  const [activeMemberships, setActiveMemberships] = useState<Membership[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchActiveMembership = async () => {
    try {
      setIsLoading(true);
      const response = await apiFetch(`/v1/memberships/my`, {
        withCredentials: true,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.details ||
            errorData.message ||
            `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      setActiveMemberships(data);
    } catch (error: any) {
      console.error('Error fetching active membership. Reason: ', error);
      toast({
        title: 'Can not fetch active membership',
        description:
          error?.message ||
          'An error occurred while fetching active membership',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    walletAddress && fetchActiveMembership();
  }, [walletAddress]);

  const hasActiveMemberships = useMemo(() => {
    return (
      activeMemberships.length > 0 &&
      activeMemberships.filter((m) => m.status === 'active').length > 0
    );
  }, [activeMemberships]);

  const hasAllMembershipsExpired = useMemo(() => {
    return activeMemberships.every((m) => m.status === 'expired');
  }, [activeMemberships]);

  const mostRecentActiveMembership = useMemo(() => {
    return activeMemberships.filter((m) => m.status === 'active')[0] ?? null;
  }, [activeMemberships]);

  return {
    activeMemberships,
    hasActiveMemberships,
    isLoading,
    hasAllMembershipsExpired,
    mostRecentActiveMembership,
  };
};
